import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import type {
  PmoDelayCauseChild,
  PmoDelayCauseFilter,
  PmoDelayCauseRow,
  PmoDelayTally,
} from '../../../types/pmo-delay.js';

/** 絞り込みの選択肢。`total` は引き直し + 遅延の合算 @graph-connects none */
const FILTERS: readonly PmoDelayCauseFilter[] = ['total', 'rework', 'delay'];

export interface CauseBreakdownCardProps {
  /** 当 Q の原因別内訳（API は合算日数の降順で返す） */
  rows: PmoDelayCauseRow[];
  /** 見出しに出す当 Q のラベル */
  quarterLabel: string;
}

/** 選択中の絞り込みに対応する日数・件数を取り出す @graph-connects none */
function tallyOf(
  row: PmoDelayCauseRow | PmoDelayCauseChild,
  filter: PmoDelayCauseFilter,
): PmoDelayTally {
  if (filter === 'rework') return row.rework;
  if (filter === 'delay') return row.delay;
  return row.total;
}

/**
 * 構成比（%）。先に 100 を掛けてから割ることで、`46 / 80 * 100` が 57.4999… になって
 * ちょうど 0.5 の境界が切り下がる浮動小数の取りこぼしを避ける。
 * @graph-connects none
 */
function sharePercent(days: number, totalDays: number): number {
  return Math.round((days * 100) / totalDays);
}

/**
 * 日数の横棒。最大値に対する比率で幅を決め、順位差を数字だけでなく形でも読ませる。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 原因別内訳テーブルの日数バー（最大値に対する相対幅）を描画する
 * @graph-connects none
 */
function DaysBar({ days, max }: { days: number; max: number }) {
  const ratio = max > 0 ? Math.min(1, days / max) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
      <div
        className="h-full rounded-full bg-amber-500"
        style={{ width: `${String(Math.round(ratio * 100))}%` }}
      />
    </div>
  );
}

/**
 * 当 Q の遅延日数を原因別に並べるテーブル。日数の多い順で、どこに時間が溶けているかを見る。
 *
 * 絞り込みは合算 / 引き直し / 遅延。絞り込みを変えると並び順もその区分の日数順に組み替える
 * （合算の順位のまま数字だけ差し替えると、上位が 0 日という読みにくい表になる）。
 *
 * `仕様追加/変更` だけシート側に ` - ` 区切りの小分類があるため、行を開くと内訳が出る。
 * 他の原因は小分類を持たないので開閉ボタン自体を出さない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO遅延の原因別内訳テーブル。合算/引き直し/遅延で絞り込め、日数の多い順に並び、仕様追加/変更は小分類を展開できる
 * @graph-connects DaysBar [calls] 日数バー描画
 * @graph-connects Card [calls] カードコンポーネント描画
 */
export function CauseBreakdownCard({ rows, quarterLabel }: CauseBreakdownCardProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<PmoDelayCauseFilter>('total');
  const [expanded, setExpanded] = useState<string | null>(null);

  // 絞り込んだ区分に件数がある行だけを、その区分の日数降順で並べ直す
  const visible = rows
    .filter((row) => tallyOf(row, filter).count > 0)
    .sort((a, b) => {
      const x = tallyOf(a, filter);
      const y = tallyOf(b, filter);
      return y.days - x.days || y.count - x.count || a.cause.localeCompare(b.cause);
    });
  // バーの基準は先頭行（=絞り込み後の最大日数）。空なら 0 で全バーが潰れる
  const first = visible[0];
  const maxDays = first ? tallyOf(first, filter).days : 0;
  const totalDays = visible.reduce((sum, row) => sum + tallyOf(row, filter).days, 0);

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={`${t('kpi.pmoDelivery.cause.title')}（${quarterLabel}）`}
        description={t('kpi.pmoDelivery.cause.description')}
        action={
          <div
            className="inline-flex items-center gap-0.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-0.5"
            role="group"
            aria-label={t('kpi.pmoDelivery.cause.filterLabel')}
          >
            {FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={filter === option}
                onClick={() => {
                  setFilter(option);
                }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
                  filter === option
                    ? 'bg-amber-500 text-white'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                {t(`kpi.pmoDelivery.cause.filter.${option}`)}
              </button>
            ))}
          </div>
        }
      />
      <CardContent>
        {visible.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('status.noData')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-xs text-[var(--color-text-muted)]">
                <th className="py-2 font-medium">{t('kpi.pmoDelivery.cause.columnCause')}</th>
                <th className="w-24 py-2 text-right font-medium">
                  {t('kpi.pmoDelivery.cause.columnDays')}
                </th>
                <th className="w-20 py-2 text-right font-medium">
                  {t('kpi.pmoDelivery.cause.columnCount')}
                </th>
                <th className="w-20 py-2 text-right font-medium">
                  {t('kpi.pmoDelivery.cause.columnShare')}
                </th>
                <th className="w-32 py-2 pl-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const tally = tallyOf(row, filter);
                const isOpen = expanded === row.cause;
                // 小分類も絞り込み後に件数が残るものだけ出す
                const children = row.children
                  .filter((child) => tallyOf(child, filter).count > 0)
                  .sort((a, b) => tallyOf(b, filter).days - tallyOf(a, filter).days);
                return [
                  <tr
                    key={row.cause}
                    className="border-b border-[var(--color-border)] last:border-0"
                  >
                    <td className="py-2 text-[var(--color-text-primary)]">
                      {children.length > 0 ? (
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          onClick={() => {
                            setExpanded(isOpen ? null : row.cause);
                          }}
                          className="text-left font-medium text-amber-600 hover:underline"
                        >
                          {isOpen ? '▾ ' : '▸ '}
                          {row.cause}
                        </button>
                      ) : (
                        row.cause
                      )}
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums text-[var(--color-text-primary)]">
                      {t('kpi.pmoDelivery.unit.days', { days: tally.days.toFixed(1) })}
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums text-[var(--color-text-secondary)]">
                      {t('kpi.pmoDelivery.cause.countValue', { count: tally.count })}
                    </td>
                    <td className="py-2 text-right font-mono tabular-nums text-[var(--color-text-muted)]">
                      {totalDays > 0 ? `${String(sharePercent(tally.days, totalDays))}%` : '-'}
                    </td>
                    <td className="py-2 pl-3 align-middle">
                      <DaysBar days={tally.days} max={maxDays} />
                    </td>
                  </tr>,
                  ...(isOpen
                    ? children.map((child) => {
                        const childTally = tallyOf(child, filter);
                        return (
                          <tr
                            key={`${row.cause}/${child.subCause}`}
                            className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] last:border-0"
                          >
                            <td className="py-1.5 pl-6 text-xs text-[var(--color-text-secondary)]">
                              {child.subCause === ''
                                ? t('kpi.pmoDelivery.cause.noSubCause')
                                : child.subCause}
                            </td>
                            <td className="py-1.5 text-right font-mono text-xs tabular-nums text-[var(--color-text-secondary)]">
                              {t('kpi.pmoDelivery.unit.days', { days: childTally.days.toFixed(1) })}
                            </td>
                            <td className="py-1.5 text-right font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
                              {t('kpi.pmoDelivery.cause.countValue', { count: childTally.count })}
                            </td>
                            <td className="py-1.5" />
                            <td className="py-1.5" />
                          </tr>
                        );
                      })
                    : []),
                ];
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
